import React, { useState, useEffect, useRef } from 'react';
import { devLog } from './utils/devTools';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, CircularProgress, Paper, Button } from '@mui/material';
import AuthenticationStep from './components/AuthenticationStep';
import CancellationCheck from './components/CancellationCheck';
import BasicJobInfo from './components/BasicJobInfo';
import DeponentInfo from './components/DeponentInfo';
import AttorneyInfo from './components/AttorneyInfo';
import ReporterPayInfo from './components/ReporterPayInfo';
import SubmitConfirmation from './components/SubmitConfirmation';
import FormNav from './components/FormNav';
import ProgressIndicator from './components/ProgressIndicator';
import Header from './components/Header';
import Footer from './components/Footer';
import DraftIndicator from './components/DraftIndicator';
import { validateToken, fetchJobData, fetchFullJobData, fetchJobByNumber } from './utils/api';
import { FormProvider, useFormContext } from './context/FormContext';
import { useAuth } from './context/AuthContext';
import { FormData as JobFormData, Deponent, Attorney, StepProps } from './types';

interface RouteParams {
  token?: string;
  jobNumber?: string;
  [key: string]: string | undefined;
}

interface AppProps {
  authenticatedFlow?: boolean;
}

interface Step {
  id: string;
  label: string;
  component: React.ComponentType<StepProps>;
}

// Component to restore step from loaded draft.
//
// Defined at module scope, NOT inside App. A component declared inside another
// component gets a new function identity on every parent render; React compares
// element types by identity, so a new identity unmounts and remounts the whole
// subtree, destroying its local state. JobLoadingGate wraps the step components,
// so declaring it inline silently reset each step's useState on every App render.
const StepRestorer: React.FC<{
  jobKey: string;
  authenticated: boolean;
  hasRestoredStepRef: React.MutableRefObject<string | null>;
  setCurrentStep: (step: number) => void;
}> = ({ jobKey, authenticated, hasRestoredStepRef, setCurrentStep }) => {
  const { draftStatus } = useFormContext();

  useEffect(() => {
    // Only restore step once per job when a draft is loaded
    if (draftStatus.savedStep !== undefined &&
        draftStatus.lastSaved && // Only if there's a saved draft (not just from auto-save)
        authenticated &&
        hasRestoredStepRef.current !== jobKey) {
      devLog('Restoring step from draft:', draftStatus.savedStep);
      setCurrentStep(draftStatus.savedStep);
      hasRestoredStepRef.current = jobKey;
    }
  }, [draftStatus.savedStep, draftStatus.lastSaved, jobKey, authenticated, hasRestoredStepRef, setCurrentStep]);

  return null;
};

// Component to show loading state while job is being initialized (including draft evaluation)
const JobLoadingGate: React.FC<{ isLoading: boolean; children: React.ReactNode }> = ({ isLoading, children }) => {
  const { draftStatus } = useFormContext();

  // Show loading state while job is being initialized
  // This includes: loading job data, checking for drafts, restoring saved state
  if (isLoading || draftStatus.isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          {draftStatus.isLoading ? 'Loading your saved progress...' : 'Preparing worksheet...'}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

const App: React.FC<AppProps> = ({ authenticatedFlow = false }) => {
  const { token, jobNumber } = useParams<RouteParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, rsrcNo } = useAuth();

  // Track whether we've already restored the step for this job
  const hasRestoredStepRef = useRef<string | null>(null);

  // Check if we're coming from the home page (via query param)
  const fromHome = new URLSearchParams(location.search).get('fromHome') === 'true';
  // Consider the flow authenticated if explicitly set OR coming from home with proper authentication
  const isAuthenticatedFlow = authenticatedFlow || (fromHome && isAuthenticated);

  const [authenticated, setAuthenticated] = useState<boolean>(isAuthenticatedFlow && isAuthenticated);
  const [jobCancelled, setJobCancelled] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(isAuthenticatedFlow ? 1 : 0); // Skip auth step if authenticated
  const [preloadedData, setPreloadedData] = useState<Partial<JobFormData> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Form steps - dynamically adjusted based on cancellation
  const getFormSteps = (): Step[] => {
    if (jobCancelled) {
      return [
        // AuthenticationStep is rendered directly in JSX (not via CurrentStepComponent);
        // the entry exists solely for the progress indicator label.
        { id: 'auth', label: 'Authentication', component: AuthenticationStep as unknown as React.ComponentType<StepProps> },
        { id: 'cancellation', label: 'Cancellation', component: CancellationCheck },
        { id: 'submit', label: 'Submit', component: SubmitConfirmation }
      ];
    } else {
      return [
        // AuthenticationStep is rendered directly in JSX (not via CurrentStepComponent);
        // the entry exists solely for the progress indicator label.
        { id: 'auth', label: 'Authentication', component: AuthenticationStep as unknown as React.ComponentType<StepProps> },
        { id: 'cancellation', label: 'Cancellation Check', component: CancellationCheck },
        { id: 'basic-info', label: 'Job Information', component: BasicJobInfo },
        { id: 'deponent-info', label: 'Deponents', component: DeponentInfo },
        { id: 'attorney-info', label: 'Attorneys', component: AttorneyInfo },
        { id: 'pay-info', label: 'Payment', component: ReporterPayInfo },
        { id: 'submit', label: 'Submit', component: SubmitConfirmation }
      ];
    }
  };

  const steps = getFormSteps();

  useEffect(() => {
    const checkToken = async () => {
      try {
        setIsLoading(true);
        // Validate token with backend
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Check if token exists by using the prefetch endpoint
        const isValid = await validateToken(token);
        if (!isValid) {
          navigate('/login');
          return;
        }

        // If token is valid, preload form data
        const data = await fetchJobData(token);
        
        // Add the token to the data
        const dataWithToken = {
          ...data,
          token // Include the token in the form data
        };
        
        // Check if we're coming from the authenticated flow (session cookie exists)
        // If so, automatically skip the PIN verification step
        if (isAuthenticated) {
          try {
            // For authenticated users, fetch full job data directly
            const fullJobData = await fetchJobByNumber(data.jobNumber?.toString() || '');
            
            devLog("Job data from authenticated fetch:", JSON.stringify(fullJobData, null, 2));
            
            if (fullJobData) {
              // First process deponents, since attorneys need the deponent IDs for transcript orders
              const deponentIds: string[] = [];
              let updatedDeponents: Deponent[] = [];
              
              // Access the full job data with proper typing for the additional fields
              const jobDataWithWitness = fullJobData as Partial<JobFormData> & { 
                witnessName?: string;
                deponentName?: string;
              };
              
              devLog("Deponent sources:", {
                "deponents array": fullJobData.deponents,
                "witnessName": jobDataWithWitness.witnessName,
                "deponentName": jobDataWithWitness.deponentName
              });
              
              // Always check for the presence of a specific deponent name field first
              if (jobDataWithWitness.witnessName || jobDataWithWitness.deponentName) {
                devLog("Using witnessName or deponentName:", 
                  jobDataWithWitness.witnessName || jobDataWithWitness.deponentName);
                
                const timestamp = Date.now();
                const id = `deponent-${timestamp}-0`;
                deponentIds.push(id);
                
                updatedDeponents = [{
                  id,
                  name: jobDataWithWitness.witnessName || jobDataWithWitness.deponentName || '',
                  exhibitStatus: '',
                  exhibitCarrier: '',
                  exhibitTrackingNumber: '',
                  readAndSign: undefined,
                  sendErrataTo: '',
                  specialProductionNotes: '',
                  numPages: 0,
                  startTimeHour: '',
                  startTimeMinute: '',
                  startTimeAmPm: '',
                  endTimeHour: '',
                  endTimeMinute: '',
                  endTimeAmPm: '',
                  startTime: '',
                  endTime: '',
                  transcriptType: 'standard',
                  transcriptFiles: [],
                  exhibitFiles: [],
                  audioFiles: [],
                  techExpert: false,
                  video: false,
                  interpreter: false,
                  afterHours: false,
                  weekendHoliday: false
                } as Deponent];
              }
              // Then check the array (reversed the order of priority)
              else if (fullJobData.deponents && Array.isArray(fullJobData.deponents) && fullJobData.deponents.length > 0) {
                // Process deponents from the array
                devLog("Processing deponents from array:", fullJobData.deponents);
                const timestamp = Date.now();
                updatedDeponents = fullJobData.deponents.map((deponent: Partial<Deponent>, index: number) => {
                  const id = `deponent-${timestamp}-${index}`;
                  deponentIds.push(id);
                  
                  return {
                    id,
                    name: deponent.name || '',
                    exhibitStatus: '',
                    exhibitCarrier: '',
                    exhibitTrackingNumber: '',
                    readAndSign: undefined,
                    sendErrataTo: '',
                    specialProductionNotes: '',
                    numPages: 0,
                    startTimeHour: '',
                    startTimeMinute: '',
                    startTimeAmPm: '',
                    endTimeHour: '',
                    endTimeMinute: '',
                    endTimeAmPm: '',
                    startTime: '',
                    endTime: '',
                    transcriptType: 'standard',
                    transcriptFiles: [],
                    exhibitFiles: [],
                    audioFiles: [],
                    techExpert: false,
                    video: false,
                    interpreter: false,
                    afterHours: false,
                    weekendHoliday: false
                  } as Deponent;
                });
              }
              // If we still don't have any deponent data, create a placeholder
              if (updatedDeponents.length === 0) {
                devLog("No deponent data found, creating placeholder");
                const timestamp = Date.now();
                const id = `deponent-${timestamp}-0`;
                deponentIds.push(id);
                
                updatedDeponents = [{
                  id,
                  name: 'Unspecified Deponent',
                  exhibitStatus: '',
                  readAndSign: undefined,
                  sendErrataTo: '',
                  specialProductionNotes: '',
                  numPages: 0,
                  startTimeHour: '',
                  startTimeMinute: '',
                  startTimeAmPm: '',
                  endTimeHour: '',
                  endTimeMinute: '',
                  endTimeAmPm: ''
                } as Deponent];
              }
              
              // Process attorneys
              let updatedAttorneys: Attorney[] = [];
              if (fullJobData.attorneys && Array.isArray(fullJobData.attorneys)) {
                const timestamp = Date.now();
                updatedAttorneys = fullJobData.attorneys.map((attorney: Partial<Attorney>, index: number) => {
                  // Create transcript orders for each deponent
                  const transcriptOrders = deponentIds.map(deponentId => ({
                    deponentId,
                    orderType: 'No Order' as const,
                    additionalServices: {
                      realtime: false,
                      roughDraft: false,
                      depositionSummary: false
                    }
                  }));
                  
                  return {
                    id: `attorney-${timestamp}-${index}`,
                    name: attorney.name || '',
                    email: attorney.email || '',
                    firm: attorney.firm || '',
                    mailingAddress: attorney.mailingAddress || '',
                    telephonic: false,
                    transcriptOrders
                  } as Attorney;
                });
              }
            
              // Format the data as needed for the form
              // Ensure we have a valid deponent name
              if (updatedDeponents.length > 0 && !updatedDeponents[0].name) {
                const deponentName = jobDataWithWitness.witnessName || 
                                    jobDataWithWitness.deponentName || 
                                    'Unspecified Deponent';
                devLog("Setting missing deponent name to:", deponentName);
                updatedDeponents[0].name = deponentName;
              }
              
              const processedData = {
                ...dataWithToken,
                ...fullJobData,
                token,
                reporterName: fullJobData.reporterName || data.reporterName,
                reporterRsrcNo: rsrcNo || undefined, // Add reporter resource number for authenticated users
                deponents: updatedDeponents,
                attorneys: updatedAttorneys
              };

              setPreloadedData(processedData);
              setAuthenticated(true); // Skip PIN verification
              setCurrentStep(1); // Move to cancellation check directly
            }
          } catch (fullDataError) {
            console.error('Could not fetch full job data despite authentication:', fullDataError);
            // Fall back to the basic data and prompt for PIN
          }
        } else {
          // Not authenticated, just use the basic data
          setPreloadedData(dataWithToken);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error validating token:', error);
        navigate('/login');
      }
    };

    const loadJobByNumber = async () => {
      try {
        setIsLoading(true);
        
        // Verify the user is authenticated
        if (!isAuthenticated || !rsrcNo) {
          navigate('/login');
          return;
        }
        
        if (!jobNumber) {
          navigate('/jobs');
          return;
        }
        
        // Fetch job data by job number
        const jobData = await fetchJobByNumber(jobNumber);
        if (!jobData) {
          setError('Job not found or you do not have access to this job');
          navigate('/jobs');
          return;
        }
        
        // Process the job data similar to what we do in handlePinVerification
        const updatedData = {
          ...jobData,
          jobNumber
        };
        
        // First process deponents, since attorneys need the deponent IDs for transcript orders
        const deponentIds: string[] = [];
        
        if (jobData.deponents && Array.isArray(jobData.deponents)) {
          // Process deponents
          const timestamp = Date.now();
          updatedData.deponents = jobData.deponents.map((deponent, index) => {
            const id = `deponent-${timestamp}-${index}`;
            deponentIds.push(id); 
            
            return {
              id,
              name: deponent.name || '',
              exhibitStatus: '',
              exhibitCarrier: '',
              exhibitTrackingNumber: '',
              readAndSign: false,
              sendErrataTo: '',
              specialProductionNotes: '',
              numPages: 0,
              startTimeHour: '',
              startTimeMinute: '',
              startTimeAmPm: '',
              endTimeHour: '',
              endTimeMinute: '',
              endTimeAmPm: '',
              startTime: '',
              endTime: '',
              transcriptType: 'standard',
              transcriptFiles: [],
              exhibitFiles: [],
              audioFiles: [],
              techExpert: false,
              video: false,
              interpreter: false,
              afterHours: false,
              weekendHoliday: false
            };
          });
        }
        
        // Process attorneys
        if (jobData.attorneys && Array.isArray(jobData.attorneys)) {
          const timestamp = Date.now();
          updatedData.attorneys = jobData.attorneys.map((attorney, index) => {
            // Create transcript orders for each deponent
            const transcriptOrders = deponentIds.map(deponentId => ({
              deponentId,
              orderType: 'No Order' as const,
              additionalServices: {
                realtime: false,
                roughDraft: false,
                depositionSummary: false
              }
            }));
            
            return {
              id: `attorney-${timestamp}-${index}`,
              name: attorney.name || '',
              email: attorney.email || '',
              firm: attorney.firm || '',
              mailingAddress: attorney.mailingAddress || '',
              telephonic: false,
              transcriptOrders
            };
          });
        }
        
        // Add reporter resource number for authenticated users
        if (rsrcNo) {
          updatedData.reporterRsrcNo = rsrcNo;
        }

        setPreloadedData(updatedData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading job by number:', error);
        setError('Error loading job data');
        navigate('/jobs');
      }
    };

    if (authenticatedFlow && jobNumber) {
      loadJobByNumber();
    } else if (token) {
      checkToken();
    } else {
      navigate('/login');
    }
  }, [token, jobNumber, navigate, authenticatedFlow, isAuthenticated, rsrcNo]);

  const handlePinVerification = async (pin: string): Promise<{ success: boolean; message?: string }> => {
    try {
      devLog("Starting PIN verification process");
      
      if (!token) {
        return { success: false, message: 'No token provided' };
      }
      
      // Fetch the full job data directly (which also verifies the PIN)
      try {
        const fullData = await fetchFullJobData(token, pin);
        devLog("Successfully retrieved full job data:", fullData);
        
        // fullData already includes the formatted reporterName from our modified fetchFullJobData
        
        // Make sure we have properly structured data for deponents and attorneys
        const updatedData = {
          ...preloadedData,
          ...fullData,
          token // Ensure token is included
        };
        
        // First process deponents, since attorneys need the deponent IDs for transcript orders
        const deponentIds: string[] = [];
        
        if (fullData.deponents && Array.isArray(fullData.deponents)) {
          // Process deponents - we only get name from the server
          const timestamp = Date.now();
          updatedData.deponents = fullData.deponents.map((deponent, index) => {
            const id = `deponent-${timestamp}-${index}`;
            deponentIds.push(id); // Store the ID for creating transcript orders
            
            return {
              id,
              name: deponent.name || '',
              exhibitStatus: '',
              exhibitCarrier: '',
              exhibitTrackingNumber: '',
              readAndSign: false,
              sendErrataTo: '',
              specialProductionNotes: '',
              numPages: 0,
              startTimeHour: '',
              startTimeMinute: '',
              startTimeAmPm: '',
              endTimeHour: '',
              endTimeMinute: '',
              endTimeAmPm: '',
              startTime: '',
              endTime: '',
              transcriptType: 'standard',
              transcriptFiles: [],
              exhibitFiles: [],
              audioFiles: [],
              techExpert: false,
              video: false,
              interpreter: false,
              afterHours: false,
              weekendHoliday: false
            };
          });
        }
        
        // Now process attorneys with transcript orders for each deponent
        if (fullData.attorneys && Array.isArray(fullData.attorneys)) {
          // Process attorneys - we only get name, email, and firm
          const timestamp = Date.now();
          updatedData.attorneys = fullData.attorneys.map((attorney, index) => {
            // Create transcript orders for each deponent
            const transcriptOrders = deponentIds.map(deponentId => ({
              deponentId,
              orderType: 'No Order' as const,
              additionalServices: {
                realtime: false,
                roughDraft: false,
                depositionSummary: false
              }
            }));
            
            return {
              id: `attorney-${timestamp}-${index}`,
              name: attorney.name || '',
              email: attorney.email || '',
              firm: attorney.firm || '',
              mailingAddress: attorney.mailingAddress || '',
              telephonic: false,
              transcriptOrders // Link transcript orders to deponents
            };
          });
        }
        
        // Add reporter resource number for authenticated users
        if (rsrcNo) {
          updatedData.reporterRsrcNo = rsrcNo;
        }

        devLog("Final data being set:", updatedData);
        setPreloadedData(updatedData);

        // Set authenticated and move to next step
        setAuthenticated(true);
        setCurrentStep(1); // Move to cancellation check
        
        return { success: true };
      } catch (error) {
        console.error("Error in PIN verification process:", error);
        
        // If it's an "Invalid PIN" error, show that to the user
        if (error instanceof Error && error.message === 'Invalid PIN') {
          return { success: false, message: 'Invalid PIN code. Please try again.' };
        }
        
        // For any other errors
        return { 
          success: false, 
          message: error instanceof Error ? error.message : 'Error verifying PIN. Please try again.' 
        };
      }
    } catch (error) {
      console.error('Unexpected error in PIN verification process:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Error verifying PIN. Please try again.' 
      };
    }
  };

  const handleCancellationResponse = (isCancelled: boolean): void => {
    setJobCancelled(isCancelled);
    
    // Only automatically advance to the next step if it's NOT cancelled
    if (!isCancelled) {
      setCurrentStep(currentStep + 1);
    }
    // If cancelled, we'll wait for the user to click the Submit Cancellation button
  };

  // Helper function for smooth scrolling (better UX on mobile)
  const scrollToTop = (): void => {
    // Use smooth scrolling on supported browsers
    try {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      // Fallback for browsers that don't support smooth scrolling
      window.scrollTo(0, 0);
    }
  };

  const goToNextStep = async (): Promise<void> => {
    if (currentStep < steps.length - 1) {
      // Auto-save draft before moving to next step (except on submit step)
      if (currentStep !== steps.length - 1) {
        // Don't block navigation on save failure, but log it
        try {
          // We'll use FormContext's saveDraft method via a ref or state
          // For now, we'll just move to the next step
          // The actual auto-save will be triggered by FormNav component
        } catch (error) {
          console.error('Error auto-saving draft:', error);
        }
      }

      setCurrentStep(currentStep + 1);
      scrollToTop();
    }
  };

  const goToPreviousStep = (): void => {
    // Special case: If we're at the Cancellation check (step 1) and we came from the
    // authenticated flow (skipped auth step), return to jobs page
    if (currentStep === 1 && isAuthenticatedFlow) {
      navigate('/jobs');
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      scrollToTop();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        bgcolor: '#f5f7fa'
      }}>
        <Header />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: '1 0 auto',
          gap: { xs: 2, sm: 2 },
          p: { xs: 3, sm: 4 }
        }}>
          <CircularProgress size={36} />
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Loading job data...
          </Typography>
        </Box>
        
        <Footer />
      </Box>
    );
  }

  const CurrentStepComponent = steps[currentStep].component;

  // Create a key that changes only when authentication status or job number changes, 
  // not when the step changes to prevent form data reset between steps
  const formProviderKey = `form-${authenticated ? 'auth' : 'unauth'}-${preloadedData?.jobNumber || 'none'}`;


  return (
    <FormProvider key={formProviderKey} initialData={preloadedData || undefined}>
      <StepRestorer
        jobKey={preloadedData?.jobNumber || 'none'}
        authenticated={authenticated}
        hasRestoredStepRef={hasRestoredStepRef}
        setCurrentStep={setCurrentStep}
      />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        bgcolor: '#f5f7fa'
      }}>
        <Header />
        
        <Container 
          maxWidth="lg" 
          sx={{ 
            py: { xs: 2, sm: 3, md: 4 }, 
            px: { xs: 2, sm: 3, md: 3 },
            flex: '1 0 auto'
          }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              bgcolor: 'transparent', 
              mb: { xs: 2, sm: 3, md: 4 }
            }}
          >
            <ProgressIndicator 
              currentStep={currentStep} 
              totalSteps={steps.length}
              stepTitles={steps.map(step => step.label)}
            />
          </Paper>
          
          <Box component="main">
            {error ? (
              <Box textAlign="center" mt={4}>
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => navigate('/jobs')}
                  sx={{ mt: 3 }}
                >
                  Return to Jobs
                </Button>
              </Box>
            ) : !authenticated ? (
              <AuthenticationStep
                onVerifyPin={handlePinVerification}
                token={token || ''}
                skipAuthMessage={isAuthenticated ?
                  "You're already authenticated. Preparing your worksheet..." : undefined}
              />
            ) : (
              <JobLoadingGate isLoading={isLoading}>
                {/* Draft indicator - show after authentication and before final submission */}
                {currentStep > 0 && currentStep < steps.length - 1 && !isSubmitted && (
                  <DraftIndicator
                    currentStep={currentStep}
                    onClearDraft={() => {
                      // Reset to beginning after clearing
                      setCurrentStep(1);
                    }}
                  />
                )}

                <CurrentStepComponent
                  onNext={goToNextStep}
                  onPrevious={goToPreviousStep}
                  onCancellationResponse={handleCancellationResponse}
                  jobCancelled={jobCancelled}
                  onSubmitSuccess={() => setIsSubmitted(true)}
                />

                {/* Show navigation for all steps except the first one (authentication) */}
                {currentStep > 0 && (
                  <FormNav
                    currentStep={currentStep}
                    totalSteps={steps.length}
                    onNext={goToNextStep}
                    onPrevious={goToPreviousStep}
                    isSubmitConfirmation={currentStep === steps.length - 1 && isSubmitted}
                  />
                )}
              </JobLoadingGate>
            )}
          </Box>
        </Container>
        
        <Footer />
      </Box>
    </FormProvider>
  );
};

export default App;